'use client';

import { useState } from 'react';
import { Search, X, ChevronDown, ChevronUp } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { cn } from '@/doi-tac-module/lib/utils';
import { useLanguage } from '@/doi-tac-module/contexts/LanguageContext';
import { agencyTranslations } from '../../constants/agency-translations';

interface FilterListWithSearchProps {
  title: string;
  items: string[];
  selectedItems: string[];
  onItemsChange: (items: string[]) => void;
  placeholder?: string;
}

export function FilterListWithSearch({
  title,
  items,
  selectedItems,
  onItemsChange,
  placeholder = 'Tìm kiếm...',
}: FilterListWithSearchProps) {
  const { language } = useLanguage();
  const t = agencyTranslations.candidateSearch;
  const [searchTerm, setSearchTerm] = useState('');
  const [isExpanded, setIsExpanded] = useState(false);

  const getTranslatedItem = (item: string) => {
    return (t.filters as any).hobbiesAndAllergies?.[item]?.[language] || item;
  };

  const filteredItems = items.filter((item) => {
    const translatedName = getTranslatedItem(item).toLowerCase();
    const originalName = item.toLowerCase();
    return translatedName.includes(searchTerm.toLowerCase()) || originalName.includes(searchTerm.toLowerCase());
  });

  const displayedItems = isExpanded ? filteredItems : filteredItems.slice(0, 5);

  const handleToggleItem = (item: string) => {
    const newSelected = selectedItems.includes(item)
      ? selectedItems.filter((i) => i !== item)
      : [...selectedItems, item];
    onItemsChange(newSelected);
  };

  const handleRemoveItem = (item: string) => {
    onItemsChange(selectedItems.filter((i) => i !== item));
  };

  return (
    <div className="space-y-3 py-2">
      <div className="flex flex-col gap-2">
        <Label className="text-sm font-semibold text-foreground/80">{title}</Label>
        
        {/* Selected Badges */}
        {selectedItems.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-1 mt-1">
            {selectedItems.map((item, idx) => (
              <Badge
                key={`selected-${item}-${idx}`}
                variant="secondary"
                className="bg-indigo-50 text-indigo-700 border-indigo-100 hover:bg-indigo-100 px-2 py-0.5 flex items-center gap-1 text-[11px] font-medium transition-colors"
                id={`badge-${item}`}
              >
                {getTranslatedItem(item)}
                <X
                  className="w-3 h-3 cursor-pointer hover:text-indigo-900"
                  onClick={() => handleRemoveItem(item)}
                />
              </Badge>
            ))}
          </div>
        )}

        {/* Search Input */}
        <div className="relative group">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground group-focus-within:text-indigo-500 transition-colors" />
          <Input
            placeholder={placeholder}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-9 h-9 text-sm bg-muted/30 border-muted-foreground/20 focus-visible:ring-indigo-500/20 focus-visible:border-indigo-500 transition-all rounded-md"
          />
        </div>
      </div>

      {/* Items List */}
      <div className="space-y-2.5 max-h-[300px] overflow-y-auto pr-1 custom-scrollbar">
        {displayedItems.length > 0 ? (
          displayedItems.map((item, idx) => (
            <div key={`item-${item}-${idx}`} className="flex items-center space-x-2.5 group cursor-pointer" onClick={() => handleToggleItem(item)}>
              <Checkbox
                id={`filter-${title}-${item}-${idx}`}
                checked={selectedItems.includes(item)}
                onCheckedChange={() => handleToggleItem(item)}
                className="border-muted-foreground/30 data-[state=checked]:bg-indigo-600 data-[state=checked]:border-indigo-600 rounded-[4px] w-4 h-4"
                onClick={(e) => e.stopPropagation()}
              />
              <Label
                htmlFor={`filter-${title}-${item}`}
                className="text-sm font-normal text-muted-foreground group-hover:text-foreground cursor-pointer transition-colors"
              >
                {getTranslatedItem(item)}
              </Label>
            </div>
          ))
        ) : (
          <p className="text-xs text-muted-foreground italic py-2">{(t.filters as any).common?.noResults?.[language] || 'Không tìm thấy kết quả'}</p>
        )}
      </div>

      {/* Show More Button */}
      {filteredItems.length > 5 && !searchTerm && (
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="text-[#19A6DF] text-sm font-medium hover:underline flex items-center gap-1 py-1 transition-all"
        >
          {isExpanded ? (
            <>
              {(t.filters as any).common?.collapse?.[language] || 'Thu gọn'} <ChevronUp className="w-3.5 h-3.5" />
            </>
          ) : (
            <>
              {(t.filters as any).common?.showMore?.[language] || 'Xem thêm'} ({items.length - 5}) <ChevronDown className="w-3.5 h-3.5" />
            </>
          )}
        </button>
      )}
    </div>
  );
}
